import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {Users } from 'lucide-react';
import { 
  Target, 
  Eye,  
  Trophy, 
  Lightbulb, 
  ArrowRight,
  Sparkles
} from 'lucide-react';
import l1 from '../assets/l1.jpg';
import l2 from '../assets/l2.jpg';

const About = () => {

  // --- ANIMATION VARIANTS ---
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      
      {/* =========================================
          HERO SECTION (Dark Premium)
      ========================================= */}
      <section className="relative pt-32 pb-24 bg-slate-900 text-white overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-20">
           <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-red-600/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
           <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-600/20 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center">
          <motion.div 
            initial="hidden" 
            animate="visible" 
            variants={fadeInUp}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
              <Sparkles size={14} className="text-yellow-400" /> Since 2012
            </div>
            <h1 className="text-4xl text-gray-100 font-serif font-bold mb-6 leading-tight">
              Beyond English.{" "}
              <span className="text-transparent bg-clip-text leading-tight bg-gradient-to-r from-red-400 to-red-600">
                We Engineer Success.
              </span>
            </h1>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              From a small classroom in Trichy to a global education powerhouse. 
              This is the story of 6th Sense Training Academy.
            </p>
          </motion.div>

          {/* Stats Bar */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12 bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6"
          >
            {[
              { label: "Students Trained", val: "17,000+" },
              { label: "Visa Success", val: "100%" },
              { label: "University Partners", val: "1000+" },
              { label: "Years Experience", val: "17+" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white mb-1">{stat.val}</div>
                <div className="text-xs text-slate-400 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* =========================================
          THE STORY (Split Layout)
      ========================================= */}
    <section className="py-20 md:py-32 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          
          {/* CONTENT SIDE (Left for better reading flow) */}
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 text-red-600 font-bold tracking-widest uppercase text-xs mb-4">
              <span className="w-8 h-[2px] bg-red-600"></span>
              Our Origin Story
            </div>
            
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 mb-8 leading-tight">
              Bridging the gap between <span className="text-red-600 decoration-red-200 underline decoration-4 underline-offset-4">Talent</span> & Opportunity.
            </h2>

            {/* The Hook */}
            <p className="text-xl text-slate-600 leading-relaxed mb-8 font-medium">
              Established with a vision to transform lives through quality education, 6th Sense began as a humble IELTS training center.
            </p>

            {/* The Problem/Solution Split */}
            <div className="space-y-6 mb-10">
              <div className="flex gap-4">
                <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center shrink-0 text-red-600">
                  <Target size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">The Gap We Saw</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mt-1">
                    Students were achieving high scores but failing to secure admissions due to poor guidance and weak documentation.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="w-12 h-12 bg-slate-900 rounded-full flex items-center justify-center shrink-0 text-white">
                  <Lightbulb size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900 text-lg">The Solution We Built</h3>
                  <p className="text-slate-500 text-sm leading-relaxed mt-1">
                    We created an ecosystem combining <strong>Elite Language Training</strong> with <strong>Premium Admission Consulting</strong>.
                  </p>
                </div>
              </div>
            </div>

            <div className="h-px bg-slate-100 w-full mb-8"></div>

            {/* Stats Row */}
            <div className="flex items-center gap-12">
              <div>
                <p className="text-4xl font-bold text-slate-900 font-serif">17+</p>
                <p className="text-xs uppercase text-slate-400 font-bold tracking-wider mt-1">Years Active</p>
              </div>
              <div className="w-px h-12 bg-slate-200"></div>
              <div>
                <p className="text-4xl font-bold text-slate-900 font-serif">7k+</p>
                <p className="text-xs uppercase text-slate-400 font-bold tracking-wider mt-1">Careers Built</p>
              </div>
            </div>
          </div>

          {/* IMAGE SIDE */}
          <div className="order-1 lg:order-2 relative">
            {/* Decorative Dot Pattern */}
            <div className="absolute -top-12 -right-12 w-64 h-64 opacity-20" 
                 style={{backgroundImage: 'radial-gradient(#ef4444 2px, transparent 2px)', backgroundSize: '32px 32px'}}>
            </div>
            
            {/* Main Image Container */}
            <div className="relative z-10">
              {/* Back offset border */}
              <div className="absolute inset-0 border-2 border-slate-900 rounded-2xl transform translate-x-6 translate-y-6 z-0 hidden md:block"></div>
              
              <div className="relative rounded-2xl overflow-hidden shadow-2xl z-10 aspect-[4/5] md:aspect-square lg:aspect-[4/5]">
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=800" 
                  alt="Team Meeting" 
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
                
                {/* Gradient Overlay for text visibility if needed, or aesthetic */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent"></div>
              </div>

              {/* Floating "Badge" Redesigned as a Card */}
              <div className="absolute -bottom-6 -left-6 md:bottom-8 md:-left-12 bg-white p-6 rounded-lg shadow-xl border-l-4 border-red-600 max-w-[200px] z-20 hidden md:block">
                <div className="flex items-start gap-3">
                  <Users className="text-slate-400 shrink-0" size={20} />
                  <p className="text-sm font-medium text-slate-600 leading-tight">
                    "We are not just consultants; we are career architects."
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

      {/* =========================================
          VALUES (Bento Grid)
      ========================================= */}
      <section className="py-20 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">The principles that guide every counselling session and every class.</p>
          </div>

          <motion.div 
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {/* Card 1 */}
            <motion.div variants={fadeInUp} className="p-8 bg-slate-50 rounded-3xl border border-slate-100 hover:border-red-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-red-600 shadow-sm mb-6">
                <Target size={24} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Precision</h3>
              <p className="text-slate-600">We don't believe in "generic" advice. Your career roadmap is engineered specifically for your profile.</p>
            </motion.div>

            {/* Card 2 */}
            <motion.div variants={fadeInUp} className="p-8 bg-slate-900 rounded-3xl border border-slate-800 text-white shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-600/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-red-600/30 transition-colors"></div>
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center text-white mb-6 backdrop-blur-sm relative z-10">
                <Eye size={24} />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 relative z-10">Transparency</h3>
              <p className="text-slate-300 relative z-10">No hidden costs. No false promises. We tell you exactly what your chances are before you pay a dime.</p>
            </motion.div>

            {/* Card 3 */}
            <motion.div variants={fadeInUp} className="p-8 bg-slate-50 rounded-3xl border border-slate-100 hover:border-red-100 hover:shadow-xl transition-all duration-300">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-red-600 shadow-sm mb-6">
                <Trophy size={24} />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">Excellence</h3>
              <p className="text-slate-600">Mediocrity is not in our syllabus. Whether it's training or documentation, we aim for the top 1%.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* =========================================
          LEADERSHIP (Human Element)
      ========================================= */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-widest uppercase text-xs mb-2 block">The Minds Behind</span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900">Meet Our Leadership</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              { name: "Kohulakrishnan", role: "Managing Director", img: l1 },
              { name: "Meena Natarajan", role: "Managing Director", img: l2 },
            ].map((person, i) => (
              <div key={i} className="group rounded-2xl p-4 border border-slate-200 hover:border-red-200 hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-40 h-40 mx-auto rounded-full overflow-hidden border-4 border-slate-50 mb-4 group-hover:scale-105 transition-transform duration-300">
                  <img src={person.img} alt={person.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-xl font-bold text-slate-900">{person.name}</h3>
                <p className="text-sm text-red-600 font-medium uppercase tracking-wide mb-4">{person.role}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          CTA SECTION
      ========================================= */}
      <section className="py-24 bg-white border-t border-slate-100">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-slate-900 mb-6">
            Ready to start your journey?
          </h2>
          <p className="text-slate-600 text-lg mb-10 max-w-2xl mx-auto">
            Your dream university is waiting. Let's build the bridge together.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-free-counselling"
              className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-10 py-4 rounded-xl font-bold transition-all hover:scale-105 shadow-xl shadow-red-900/20"
            >
              Book Free Counselling <ArrowRight size={18} />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border-2 border-slate-200 hover:border-red-600 hover:text-red-600 px-10 py-4 rounded-xl font-bold transition-all"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;
